/**
 * calendar-period — the date control's pure logic: what the period is called, whether Today has anywhere to go, and
 * how far ‹ › move. Same naming as the Practice calendar ("12 – 18 Oct 2026", "October 2026", the long day); the
 * steps are the Signal DS sub-toolbar's own (a week on Week, a day everywhere else), so nothing about navigation
 * changes, only where the controls sit.
 */
import type { DateFilter } from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
] as const;

function shiftDate(d: Date, deltaDays: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + deltaDays);
  return next;
}

/** The Monday of the week holding `d` (the Week view runs Monday to Sunday). */
export function mondayOf(d: Date): Date {
  return shiftDate(d, -((d.getDay() + 6) % 7));
}

/**
 * One step of ‹ ›, as the Practice calendar does it: a month on Month (the 1st of the next / previous month), seven
 * days on Week, one day on Day, Room and Doctor (the single-day splits).
 */
export function stepPeriod(d: Date, view: DateFilter, dir: -1 | 1): Date {
  if (view === 'Month') return new Date(d.getFullYear(), d.getMonth() + dir, 1);
  return shiftDate(d, (view === 'Week' ? 7 : 1) * dir);
}

/** The period the grid shows, named: "12 – 18 Oct 2026" (Week), "October 2026" (Month), else the long day. */
export function periodLabel(d: Date, view: DateFilter): string {
  if (view === 'Month') return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
  if (view === 'Week') {
    const a = mondayOf(d);
    const b = shiftDate(a, 6);
    return a.getMonth() === b.getMonth()
      ? `${a.getDate()} – ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`
      : `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} – ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
  }
  return d.toLocaleDateString(undefined, { month: 'long', day: '2-digit', year: 'numeric' });
}

/** True when today falls inside the period shown, so Today has nowhere to go. */
export function showsToday(d: Date, view: DateFilter, now: Date = practiceNow()): boolean {
  if (view === 'Month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (view === 'Week') return mondayOf(d).toDateString() === mondayOf(now).toDateString();
  return d.toDateString() === now.toDateString();
}
